/**
 * harbourPupils.js
 * ----------------
 * Pure view-model helpers for the Harbour SENCo portal (SEN & ND Access
 * Portal, TDI-53). They translate RMS referral data into the states the
 * Harbour pupil cards, lanes and record sheets show. Statuses and labels are
 * the READY FOR DEV design vocabulary; the underlying signals are the same
 * ones the legacy service-user dashboard reads (referralStatus.js).
 */

import {
  STATUS,
  hfpInstrumentForRecord,
  recordRequiresSubmissionConfirmation,
  hfpReportConfirmationsIn,
} from './referralStatus';
import { pathwayReviews, pathwayTriage, pathwayTriageDocuments, pathwaysForConcern, PATHWAY_LABELS } from './triagePathways';
import { getTriageDecisionSummary, isHfpFormKey } from './referralStatus';
import { evaluateYearGroup } from '../constants/pilotEligibility';

/**
 * Map a role's form-completion status (STATUS.*) onto a Harbour status chip.
 * The chip keeps its word even for pre-form states (invite sent, not yet
 * nominated) via the label override, so meaning never rides on colour alone.
 */
export function roleFormChip(roleStatus) {
  switch (roleStatus) {
    case STATUS.COMPLETED:
      return { status: 'done', label: 'Done' };
    case STATUS.STARTED:
      return { status: 'inProgress', label: 'In progress' };
    case STATUS.INVITE_SENT:
      return { status: 'notStarted', label: 'Invite sent' };
    case STATUS.NOT_ASSIGNED:
      return { status: 'notStarted', label: 'Not invited yet' };
    case STATUS.NOT_STARTED:
    default:
      return { status: 'notStarted', label: 'Not started' };
  }
}

/**
 * Classify one referral pathway for the school-user view.
 *
 * Mirrors getServiceUserReferralStatus / classifyReferralStage semantics, per
 * pathway (C8: combined referrals are triaged independently per pathway):
 * - a settled outcome whose letter has been sent to the viewer's role shows as
 *   outcome available (accepted) or closed (not accepted);
 * - a settled outcome whose letter has NOT been sent yet is still in review:
 *   while the referral waits on letter sign-off and the admin-handover send,
 *   nothing is available to the viewer, and the view-letter action (gated on
 *   letterSharedWithRole) is rightly hidden — the chip must not promise an
 *   outcome the record cannot show (SENCO feedback, 22 Oct);
 * - any clinician review activity shows as in review;
 * - a referral in the triage queue (marked ready, or every requested form in)
 *   shows as sent, waiting for review;
 * - otherwise the referral is still in progress.
 */
/** The chip states that mean a pathway has stopped. */
export const STOPPED_PATHWAY_STATES = [
  'stoppedNoConsent',
  'stoppedConsentWithdrawn',
  'stoppedConsentWithdrawnByFamily',
];

/**
 * Whether a classifier result means the pathway has stopped. Asked rather
 * than compared against literals, because the list grew (TDI-474) and the
 * screens that compared by hand silently fell through to the live timeline.
 */
export function isStoppedPathwayState(state) {
  return STOPPED_PATHWAY_STATES.includes(state);
}

/**
 * How far a pathway got on its own timeline, ignoring whether it was stopped.
 *
 * A stopped referral keeps the stage it reached: the board column and the
 * stage strip are drawn from that stage, while the chip and the banner carry
 * the stopping. Collapsing both into one value is what made a stopped pupil
 * vanish from the board and the strip fall back to In progress, so the two
 * questions are answered separately — this one is "how far did it get",
 * classifyPathwayForSchoolUser is "what does the chip say".
 */
export function livePathwayStateForSchoolUser({
  reviews = [],
  triageStatus = null,
  readyForTriage = false,
  allFormsIn = false,
  letterShared = false,
} = {}) {
  const triageComplete = String(triageStatus || '') === 'complete';
  const { outcome } = getTriageDecisionSummary(reviews, { triageComplete });
  if (outcome === 'accepted') return letterShared ? 'outcomeAvailable' : 'inReview';
  if (outcome === 'discharged') return letterShared ? 'closed' : 'inReview';

  const underway =
    triageStatus === 'in_triage' ||
    triageStatus === 'pending_second_review' ||
    (Array.isArray(reviews) && reviews.length > 0);
  if (underway) return 'inReview';

  if (readyForTriage || allFormsIn) return 'sentWaitingForReview';
  return 'inProgress';
}

export function classifyPathwayForSchoolUser({
  reviews = [],
  triageStatus = null,
  readyForTriage = false,
  allFormsIn = false,
  letterShared = false,
  stopped = false,
  stopReason = null,
} = {}) {
  // A stopped referral wins over everything else it could be (TDI-474). The
  // school and the family read the same answer from here, so neither can be
  // told the referral is still moving while the other is told it has stopped
  // — and a SENCO is not left chasing a parent for forms nobody will use.
  if (stopped) {
    return stopReason === 'no_consent'
      ? 'stoppedNoConsent'
      : 'stoppedConsentWithdrawnByFamily';
  }
  return livePathwayStateForSchoolUser({
    reviews, triageStatus, readyForTriage, allFormsIn, letterShared,
  });
}

/**
 * Build the referral-lane rows for a pupil: one row per triage pathway the
 * referral runs on (ADHD, Autism, or both), each with its Harbour chip state.
 */
export function buildReferralPathwayRows(patient, { readyForTriage = false, allFormsIn = false } = {}) {
  if (!patient) return [];
  const keys = pathwaysForConcern(patient.intakeConcern);
  if (keys.length === 0) return [];
  const pathwayState = patient.referralPathway || {};
  return keys.map((key) => {
    const live = {
      reviews: pathwayReviews(patient, key),
      triageStatus: pathwayTriage(patient, key)?.status || null,
      readyForTriage,
      allFormsIn,
      letterShared: letterSharedWithRole(patient, key, 'school_user'),
    };
    return {
      key,
      label: PATHWAY_LABELS[key],
      status: classifyPathwayForSchoolUser({
        ...live,
        stopped: pathwayState.status === 'stopped',
        stopReason: pathwayState.stopReason || null,
      }),
      // Carried beside the chip state so a stopped referral still knows how
      // far it got: the board column and the stage strip read this, the chip
      // reads `status`.
      stageReached: referralStageFor(livePathwayStateForSchoolUser(live)),
    };
  });
}

/**
 * Whether a referral has left the school: any pathway has moved past being
 * filled in (the pupil record's rule). From then on the forms are locked.
 */
export function isReferralSent(pathways) {
  return (pathways || []).some((p) => p.status !== 'inProgress');
}

/**
 * The referral lines on a pupil card and table row (TDI-694, 3192:111089):
 * one line per pathway with its chip, then, until the referral is sent, the
 * school and parent or carer forms rows. Once it is sent the forms are
 * nothing the school can act on, so a sent referral is one line per pathway,
 * as the record already shows it.
 */
export function referralLaneRows(pathways, formRows = []) {
  const pathwayRows = (pathways || []).map((p) => ({
    key: `pathway-${p.key}`,
    pathwayKey: p.key,
    label: p.label,
    emphasis: true,
    chip: { status: p.status, tone: 'referral' },
  }));
  return isReferralSent(pathways) ? pathwayRows : [...pathwayRows, ...formRows];
}

/**
 * The date line on a referral board card (TDI-694, 3192:183326): "Sent
 * 3 November 2026" in Sent, waiting for review, from the readiness marker
 * (the date the referral reached the service); "Picked up 5 November 2026"
 * in In review, from that pathway's latest triage pickup. Other columns, and
 * records without the stamp, show no date.
 */
export function referralBoardDateLine(patient, pathwayKey, stage) {
  if (stage === 'sentWaitingForReview') {
    const sentOn = formatLongDate(patient?.triageReadiness?.markedAt);
    return sentOn ? `Sent ${sentOn}` : null;
  }
  if (stage === 'inReview') {
    const pickedUpOn = formatLongDate(pathwayTriage(patient, pathwayKey)?.assignedAt);
    return pickedUpOn ? `Picked up ${pickedUpOn}` : null;
  }
  return null;
}

/**
 * England school year group from a date of birth: the school year starting
 * 1 September after the child's fourth birthday is Reception, then Year 1..13.
 * Mechanical date arithmetic only — returns null rather than guessing when
 * the date is missing, unparseable, or out of school age range.
 */
export function deriveYearGroup(dateOfBirth, now = new Date()) {
  if (!dateOfBirth) return null;
  const dob = new Date(dateOfBirth);
  if (Number.isNaN(dob.getTime())) return null;

  // The school year that `now` falls in starts on 1 September.
  const schoolYearStart = now.getMonth() >= 8 ? now.getFullYear() : now.getFullYear() - 1;
  // Age the child turns in the calendar year the school year starts, adjusted
  // down for autumn-born children (born Sept-Dec start school a year later).
  const bornAfterAugust = dob.getMonth() >= 8;
  const ageAtSept1 = schoolYearStart - dob.getFullYear() - (bornAfterAugust ? 1 : 0);

  const yearNumber = ageAtSept1 - 4; // 4 at 1 Sept -> Reception (year 0)
  if (yearNumber < 0 || yearNumber > 13) return null;
  return yearNumber === 0 ? 'Reception' : `Year ${yearNumber}`;
}

/**
 * The school's phase as its own pupils show it: 'primary' or 'secondary' when
 * every pupil with a phase shares it, else null. There is no school-level
 * setting; the server infers a new pupil's phase the same way
 * (_inherited_school_phase).
 */
export function schoolPhaseOfPupils(pupils) {
  const phases = new Set((pupils || [])
    .map((p) => (p && p.patient && p.patient.schoolPhase) || null)
    .filter((ph) => ph === 'primary' || ph === 'secondary'));
  return phases.size === 1 ? [...phases][0] : null;
}

/**
 * Pupils in register order: surname, then first name, case-insensitive
 * (Your pupils, 3192:111089). The surname comes from the record where it is
 * held, else the last word of the display name.
 */
export function sortPupilsBySurname(pupils) {
  const parts = (p) => {
    const patient = p.patient || {};
    const words = String(p.name || '').trim().split(/\s+/);
    const last = patient.subject_last_name || patient.lastName || patient.subject?.lastName || words[words.length - 1] || '';
    const first = patient.subject_first_name || patient.firstName || patient.subject?.firstName || words[0] || '';
    return [last.toLowerCase(), first.toLowerCase()];
  };
  return [...(pupils || [])].sort((a, b) => {
    const [aLast, aFirst] = parts(a);
    const [bLast, bFirst] = parts(b);
    return aLast.localeCompare(bLast) || aFirst.localeCompare(bFirst);
  });
}

/**
 * Whether a pathway's outcome letter has been sent to a service-user role
 * ('guardian' | 'school_user'): the PDF exists and the clinician's send listed
 * the role. The same rule rmsFetchTriageDocument enforces, so a link shown
 * here always opens. Combined referrals hold one letter per pathway.
 */
export function letterSharedWithRole(patient, pathwayKey, role) {
  const docs = pathwayTriageDocuments(patient, pathwayKey) || patient?.triageDocuments || null;
  if (!docs) return false;
  const shared = Array.isArray(docs.sharedRoles) ? docs.sharedRoles : [];
  return Boolean(docs?.documentPaths?.pdf) && shared.includes(role);
}

/**
 * A recorded moment as a Date: a Firestore Timestamp, a { seconds } map, a
 * Date, epoch milliseconds or an ISO string. Null when missing or unreadable,
 * so a screen shows no date line rather than "Invalid Date".
 */
export function toDateValue(value) {
  if (value === null || value === undefined || value === '') return null;
  let d = null;
  if (typeof value.toDate === 'function') d = value.toDate();
  else if (value instanceof Date) d = value;
  else if (typeof value === 'object' && typeof value.seconds === 'number') d = new Date(value.seconds * 1000);
  else if (typeof value === 'number' || typeof value === 'string') d = new Date(value);
  return d && !Number.isNaN(d.getTime()) ? d : null;
}

/** "17 July 2026": the long date the record and the board use. */
export function formatLongDate(value) {
  const d = toDateValue(value);
  return d ? d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : null;
}

/**
 * When a pathway's outcome letter was sent (TDI-691): the `sentAt` that
 * rmsPublishTriageLetter stamps on every send, so a letter sent again shows
 * the latest send. Null for a letter sent before the stamp existed. Callers
 * show it only once letterSharedWithRole says the letter is theirs to open.
 */
export function letterSentAt(patient, pathwayKey) {
  const docs = pathwayTriageDocuments(patient, pathwayKey) || patient?.triageDocuments || null;
  return toDateValue(docs && docs.sentAt);
}

/**
 * The letter button's words for a pathway (TDI-691): "View ADHD letter",
 * "View autism letter" (lower case mid-sentence). Both audiences use it.
 */
export function viewLetterLabel(pathwayKey) {
  const word = pathwayKey === 'asd' ? 'autism' : (PATHWAY_LABELS[pathwayKey] || '');
  return word ? `View ${word} letter` : 'View letter';
}

/**
 * Secondary-age pupils (Year 7 and above) never do the HFP, so the SEND
 * pathway is not offered to them. The record's school phase decides; a
 * record without one falls back to its year group.
 */
export function isSecondaryPupil(pupil) {
  const phase = pupil?.patient?.schoolPhase;
  if (phase === 'secondary') return true;
  if (phase === 'primary') return false;
  const match = /^Year (\d+)$/.exec(pupil?.yearGroup || pupil?.patient?.yearGroup || '');
  return Boolean(match && Number(match[1]) >= 7);
}

/**
 * The year group the eligibility rules judge (TDI-488): the recorded one, or
 * the one the date of birth puts the pupil in today — the same derivation
 * used when the school leaves the year group blank. Null when neither is
 * usable, which fails eligibility closed (YEAR_GROUP_MISSING).
 */
export function effectiveYearGroup(pupil, now = new Date()) {
  return (
    pupil?.yearGroup
    || pupil?.patient?.yearGroup
    || deriveYearGroup(pupil?.patient?.dateOfBirth || pupil?.patient?.subject?.dob, now)
    || null
  );
}

/**
 * Pilot eligibility of the SEND pathway for this pupil (TDI-488; SOP v0.3
 * section 8.2: Key Stage 1 and 2 only — Reception and secondary are refused).
 * @returns {{ eligible: boolean, reasonCode: string | null }}
 */
export function senPathwayEligibility(pupil, now = new Date()) {
  return evaluateYearGroup('sen', effectiveYearGroup(pupil, now), now);
}

/** True when the SEND pathway may be offered to this pupil (Years 1 to 6). */
export function isSenPathwayEligible(pupil, now = new Date()) {
  return senPathwayEligibility(pupil, now).eligible;
}

function participantCreatedMs(p) {
  const at = p && p.createdAt;
  if (!at) return Number.MAX_SAFE_INTEGER;
  if (typeof at.toMillis === 'function') return at.toMillis();
  if (typeof at.toDate === 'function') return at.toDate().getTime();
  const secs = at._seconds ?? at.seconds;
  if (typeof secs === 'number') return secs * 1000;
  const ms = new Date(at).getTime();
  return Number.isNaN(ms) ? Number.MAX_SAFE_INTEGER : ms;
}

/**
 * The pupil's primary parent or carer: the one added first, which is the one
 * entered with the pupil (Add a pupil). Ties keep the list's order.
 */
export function primaryGuardian(guardians) {
  const list = (guardians || []).filter((g) => g && g.status !== 'superseded');
  if (!list.length) return null;
  return list
    .map((g, i) => ({ g, i, ms: participantCreatedMs(g) }))
    .sort((a, b) => a.ms - b.ms || a.i - b.i)[0].g;
}

/**
 * The parent or carer a pathway's section names and sends to: the SEND
 * pathway uses the primary parent; the referral pathway uses the parent the
 * referral nominated, falling back to the primary parent.
 */
export function pathwayGuardian(pupil, pathway) {
  const guardians = (pupil?.guardians || []).filter((g) => g && g.status !== 'superseded');
  if (pathway === 'referral' && pupil?.informantParticipantId) {
    const nominated = guardians.find((g) => (g.participantId || g.id) === pupil.informantParticipantId);
    if (nominated) return nominated;
  }
  return primaryGuardian(guardians);
}

/** The dashboard's filter-tab keys, in display order. */
export const PUPIL_FILTERS = ['all', 'sen', 'referral', 'none'];

/**
 * Count pupils per filter tab and apply the active filter.
 * A pupil is "on" a pathway when that lane is active on their card.
 */
export function filterPupils(pupils, activeFilter) {
  const onSen = (p) => Boolean(p.sen);
  const onReferral = (p) => Boolean(p.referral && p.referral.pathways && p.referral.pathways.length);
  const counts = {
    all: pupils.length,
    sen: pupils.filter(onSen).length,
    referral: pupils.filter(onReferral).length,
    none: pupils.filter((p) => !onSen(p) && !onReferral(p)).length,
  };
  let visible = pupils;
  if (activeFilter === 'sen') visible = pupils.filter(onSen);
  else if (activeFilter === 'referral') visible = pupils.filter(onReferral);
  else if (activeFilter === 'none') visible = pupils.filter((p) => !onSen(p) && !onReferral(p));
  return { counts, visible };
}

// ─────────────────────────────────────────────────────────────────────────────
// SEN pathway view-model
// ─────────────────────────────────────────────────────────────────────────────

/** Stage labels in strip order. Both pathways have four stages. */
export const SEN_STAGES = ['assess', 'plan', 'do', 'review'];
export const SEN_STAGE_LABELS = {
  assess: 'Assess', plan: 'Plan', do: 'Do', review: 'Review',
};
/*
 * Four stages, ending at Outcome available. The Closed column was retired on
 * 19 Oct: a decided referral rests at its outcome rather than sitting in a
 * column of its own. 'closed' is still a status a record can hold, so it
 * keeps its label for the chips and is shown here at the outcome it reached.
 * A stopped referral stays in the column it reached too — the design keeps
 * the card on the board and counts it in the column header (4490:339686),
 * with the stopping carried by the chips.
 */
export const REFERRAL_STAGES = [
  'inProgress', 'sentWaitingForReview', 'inReview', 'outcomeAvailable',
];
/** The stage a status occupies on the strip and the board. */
export function referralStageFor(status) {
  return status === 'closed' ? 'outcomeAvailable' : status;
}

/**
 * Where a pathway row sits on the board and the stage strip.
 *
 * A stopped referral holds its stopped state in `status`, which names no
 * stage, so the stage it reached is read from `stageReached`. Rows built by
 * hand in older tests carry no `stageReached`, hence the fallback.
 */
export function referralBoardStage(pathway = {}) {
  return pathway.stageReached || referralStageFor(pathway.status);
}

export const REFERRAL_STAGE_LABELS = {
  inProgress: 'In progress',
  sentWaitingForReview: 'Sent, waiting for review',
  inReview: 'In review',
  outcomeAvailable: 'Outcome available',
  closed: 'Closed',
};

/**
 * Whether an HFP report can be produced/viewed for this record.
 *
 * Only the record's own instrument's scoring counts (hfpInstrumentForRecord):
 * a v4 record needs `domains.hfpV4.scoring`, a v3 record (Harbour pupils by
 * default) needs v3 — a stale scoring of another generation must not offer a
 * report built from forms the child was never asked. v2 records accept
 * either, as legacy referrals always have. Mirrors the report gate in
 * rmsHarbourSetSenPlanAction (rms_harbour.py).
 */
export function hasHfpReport(patient) {
  // Records gated on the confirm-and-submit step (TDI-418) only have a report
  // once both respondents have confirmed: the deterministic scoring write
  // lands at the last form submission, but the report itself (summaries,
  // TDI-416/417) is only generated at confirmation — showing Plan and
  // "Report available" from the scoring alone announced a report nobody had
  // submitted yet (staging E2E feedback, 26 Oct). Legacy records keep the
  // completion-is-the-decision rule, mirroring the backend. The
  // confirmations that count are the SEND pathway's own, never the
  // referral's (TDI-651).
  if (recordRequiresSubmissionConfirmation(patient)
      && !hfpReportConfirmationsIn(patient)) {
    return false;
  }
  const domains = (patient && patient.domains) || {};
  const v3 = Boolean(domains.hfpV3 && domains.hfpV3.scoring);
  const instrument = hfpInstrumentForRecord(patient);
  if (instrument === 'v4') return Boolean(domains.hfpV4 && domains.hfpV4.scoring);
  if (instrument === 'v3') return v3;
  return Boolean(domains.hfpV2 && domains.hfpV2.scoring) || v3;
}

/**
 * The SEN pathway stage for a record:
 * - 'stopped' when the pathway was stopped (reason on senPathway.stopReason);
 * - an explicit Do/Review stage when one has been recorded;
 * - 'plan' once the HFP report exists (the report is what Plan is about);
 * - 'assess' while forms are still being gathered;
 * - null when the pupil is not on the SEN pathway.
 */
export function senStage(patient) {
  const sen = (patient && patient.senPathway) || null;
  if (!sen || sen.status === 'not_active' || !sen.status) return null;
  if (sen.status === 'stopped') return 'stopped';
  return senStageReached(patient);
}

/**
 * The SEN stage a record reached, ignoring whether the pathway was stopped.
 *
 * 'stopped' is no stage, so a stopped pupil matched no board column and
 * dropped off the SEND board entirely. The board and the stage strip read
 * this; the stopping is carried by the chips and the lane's stopReason.
 */
export function senStageReached(patient) {
  const sen = (patient && patient.senPathway) || null;
  if (!sen || sen.status === 'not_active' || !sen.status) return null;
  if (sen.stage === 'do' || sen.stage === 'review') return sen.stage;
  if (hasHfpReport(patient)) return 'plan';
  return 'assess';
}

/**
 * Build the SEN-lane view model for a pupil card / record section.
 * Returns null when the pathway is not active (the lane renders "Not active").
 */
/**
 * The teachers holding school forms on one lane of a pupil card (TDI-489):
 * the strengths and needs forms belong to the SEND lane while SEND is active
 * and to the referral otherwise, every other form to the referral.
 * @param {Array<{firstName?: string, lastName?: string, email?: string, allocations?: object}>} teachers
 * @param {'sen'|'referral'} lane
 * @param {boolean} senActive
 * @returns {string[]} display names, without repeats
 */
export function teacherNamesForLane(teachers, lane, senActive) {
  const onLane = (formKey) => {
    const senForm = senActive && isHfpFormKey(formKey);
    return lane === 'sen' ? senForm : !senForm;
  };
  return [...new Set((teachers || [])
    .filter((t) => Object.keys(t.allocations || {}).some(onLane))
    .map((t) => [t.firstName, t.lastName].filter(Boolean).join(' ') || t.email))];
}

// The profile row's status after the report: the furthest step ticked, Plan
// then Do then Review (rmsHarbourSetSenPlanAction). Do and Review wording is
// interim, pending Tom and Jasmine.
export const SEN_PLAN_STATUS_LABELS = {
  reportAvailable: 'Report available',
  planCreated: 'Plan created',
  supportInPlace: 'Support in place',
  reviewHeld: 'Reviewed',
};

export function senPlanStatus(patient) {
  const plan = ((patient && patient.senPathway) || {}).plan || {};
  return ['reviewHeld', 'supportInPlace', 'planCreated'].find((a) => plan[a]) || 'reportAvailable';
}

/** The chip a stopped SEND pathway carries, on the pupil card and the board alike. */
export function senStoppedChips(sen) {
  if (!sen || sen.stage !== 'stopped') return [];
  return [{
    status: 'stoppedConsentWithdrawn',
    label: sen.stopReason === 'consent_withdrawn' ? 'Stopped: consent withdrawn' : 'Stopped',
    tone: 'neutral',
  }];
}

export function buildSenLane(patient, { schoolStatus, guardianStatus } = {}) {
  const stage = senStage(patient);
  if (!stage) return null;
  if (stage === 'stopped') {
    return {
      stage,
      stageReached: senStageReached(patient),
      stageLabel: 'Stopped',
      stopReason: (patient.senPathway || {}).stopReason || null,
      rows: [],
      reportAvailable: hasHfpReport(patient),
    };
  }
  // After the report the profile row carries the furthest step ticked instead
  // of repeating "Report available", so the card and board match the record.
  const planStatus = senPlanStatus(patient);
  const rows = stage === 'assess'
    ? [
        { key: 'school-forms', label: 'School forms', chip: { ...roleFormChip(schoolStatus), tone: 'neutral' } },
        { key: 'parent-forms', label: 'Parent or carer forms', chip: { ...roleFormChip(guardianStatus), tone: 'neutral' } },
      ]
    : [
        // No row label: the chip sits directly under the stage word rather than
        // behind a "Profile" column that says nothing the chip does not.
        { key: 'report', label: '', chip: { status: planStatus, label: SEN_PLAN_STATUS_LABELS[planStatus], tone: 'sen' } },
      ];
  return {
    stage,
    stageReached: stage,
    stageLabel: SEN_STAGE_LABELS[stage],
    stopReason: null,
    rows,
    reportAvailable: hasHfpReport(patient),
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Filters (tabs + year group panel)
// ─────────────────────────────────────────────────────────────────────────────

/** Sorted unique year groups present in the pupil list (Reception first). */
export function yearGroupOptions(pupils) {
  const seen = new Set();
  for (const p of pupils || []) {
    if (p.yearGroup) seen.add(p.yearGroup);
  }
  const rank = (yg) => (yg === 'Reception' ? 0 : Number(String(yg).replace('Year ', '')) || 99);
  return Array.from(seen).sort((a, b) => rank(a) - rank(b));
}

/**
 * Apply the active filter tab and the filters-panel selections.
 * @param {Array} pupils
 * @param {string} activeFilter - 'all' | 'sen' | 'referral' | 'none'
 * @param {{ yearGroups?: string[] }} [panel] - filters-panel selections
 */
export function filterPupilsWithPanel(pupils, activeFilter, panel = {}) {
  const { counts, visible } = filterPupils(pupils, activeFilter);
  const yearGroups = panel.yearGroups || [];
  const panelled = yearGroups.length
    ? visible.filter((p) => yearGroups.includes(p.yearGroup))
    : visible;
  return { counts, visible: panelled };
}

// ─────────────────────────────────────────────────────────────────────────────
// Overview boards
// ─────────────────────────────────────────────────────────────────────────────

/**
 * SEN overview board: one column per stage, one card per pupil on the pathway.
 */
export function senBoardColumns(pupils) {
  const columns = SEN_STAGES.map((stage) => ({
    key: stage,
    label: SEN_STAGE_LABELS[stage],
    cards: [],
  }));
  const byStage = Object.fromEntries(columns.map((c) => [c.key, c]));
  for (const pupil of pupils || []) {
    // The stage reached, so a stopped pupil stays in the column they got to
    // rather than matching no column and dropping off the board.
    const stage = pupil.sen && (pupil.sen.stageReached || pupil.sen.stage);
    if (stage && byStage[stage]) {
      byStage[stage].cards.push({
        id: pupil.id,
        name: pupil.name,
        yearGroup: pupil.yearGroup,
      });
    }
  }
  return columns;
}

/**
 * Referral overview board: one column per referral state, one card per
 * referral pathway (a pupil referred for both appears once per pathway).
 */
export function referralBoardColumns(pupils) {
  const columns = REFERRAL_STAGES.map((stage) => ({
    key: stage,
    label: REFERRAL_STAGE_LABELS[stage],
    cards: [],
  }));
  const byStage = Object.fromEntries(columns.map((c) => [c.key, c]));
  for (const pupil of pupils || []) {
    for (const pathway of (pupil.referral && pupil.referral.pathways) || []) {
      const stage = referralBoardStage(pathway);
      const col = byStage[stage];
      if (col) {
        col.cards.push({
          id: pupil.id,
          name: pupil.name,
          yearGroup: pupil.yearGroup,
          pathwayLabel: pathway.label,
          pathwayKey: pathway.key,
          dateLine: referralBoardDateLine(pupil.patient, pathway.key, stage),
        });
      }
    }
  }
  return columns;
}
